import { cloneElement, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import {
  useFloating, autoUpdate, offset, flip, shift,
  useHover, useFocus, useClick, useDismiss, useRole, useInteractions,
  safePolygon, FloatingPortal, useTransitionStyles,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import './floating.scss';

// Uncontrolled hover/click label tooltip (replaces the @tippyjs/react "tooltip"
// variation). Interactive menus use Popover instead.

export type TooltipTrigger = 'hover' | 'click' | 'hover-click';

export interface TooltipProps {
  content: ReactNode;
  placement?: Placement;
  disabled?: boolean;
  trigger?: TooltipTrigger;
  /** Extra class on the floating root (e.g. 'mypart-tooltip'). */
  popperClassName?: string;
  children: ReactElement<Record<string, unknown>>;
}

export default function Tooltip({
  content, placement = 'top', disabled = false, trigger = 'hover', popperClassName, children,
}: TooltipProps) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  const hover = useHover(context, {
    enabled: trigger === 'hover' || trigger === 'hover-click',
    move: false,
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, { enabled: trigger === 'hover' || trigger === 'hover-click' });
  const click = useClick(context, { enabled: trigger === 'click' || trigger === 'hover-click' });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss, role]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: 150, close: 100 },
    initial: { opacity: 0, transform: 'scale(0.96)' },
  });

  if (disabled || content == null || content === '') {
    return children;
  }

  return (
    <>
      {/* child props first, floating ref last (a child ref must not clobber it) */}
      {cloneElement(children, getReferenceProps({ ...children.props, ref: refs.setReference }))}
      {isMounted && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 9999 }} className={popperClassName} {...getFloatingProps()}>
            <div className="sh-floating sh-floating--dark" style={transitionStyles}>
              <div className="sh-floating__content">{content}</div>
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
